import { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

/**
 * The options of a clarification, as things you can click.
 *
 * ## Why this is in ui/common and not in the Gibbs tree
 *
 * It began as `gibbs/ui/WorkflowPane/WorkflowClarification.jsx`, whose own docstring said it
 * "can be used in both ChatBot and WorkflowPane contexts" -- which it could not. ChatBot is not
 * a registered seam of the Gibbs import boundary (test/bmaps/gibbsImportBoundary.test.js), so a
 * component inside `gibbs/` is one ChatBot may never import. The component was written for a
 * caller that was structurally forbidden to use it, and so nothing ever did: every clarification
 * rendered as a markdown bullet list the user had to retype.
 *
 * Nothing here knows anything about Gibbs, so it lives outside the boundary and both sides may
 * import it.
 *
 * ## Clicking a chip TYPES the answer
 *
 * `onSelect` is handed the option's label, and ChatBot sends it exactly as if the user had typed
 * it. That is deliberate rather than lazy:
 *
 *   - the answer lands in the transcript as a user message, so the conversation keeps its
 *     context -- a click that resumed a cascade silently would leave a gap where the user's
 *     decision should be, and later turns read that history;
 *   - there is ONE answer path, so a clicked answer and a typed answer cannot diverge. The
 *     label-matching in ChatBot's `answerFrom` is an exact comparison, which a chip satisfies by
 *     construction and a human satisfies only by luck.
 *
 * @param {Object} props
 * @param {Array<{label: string, detail?: string, input?: boolean, placeholder?: string}>}
 *   props.options
 * @param {function(string): void} props.onSelect - called with the text to send
 * @param {boolean} [props.disabled] - once answered, the chips stay visible but stop responding
 */
export default function ClarificationChips({ options, onSelect, disabled = false }) {
    const [typed, setTyped] = useState('');
    const list = Array.isArray(options) ? options : [];
    if (list.length === 0) return null;

    // `input: true` marks an option that needs a value rather than a choice -- "Type a protein
    // or PDB ID". The flag has been in the clarification builders all along, read by nothing.
    const chips = list.filter((o) => !o.input);
    const inputs = list.filter((o) => o.input);

    const send = (text) => {
        const value = (text || '').trim();
        if (!value || disabled) return;
        onSelect(value);
    };

    return (
        <Box sx={{
            p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.75,
        }}
        >
            {chips.length > 0 && (
                <Box sx={{
                    display: 'flex', flexWrap: 'wrap', gap: 0.5,
                }}
                >
                    {chips.map((opt, i) => (
                        <Chip
                            // Index keys: an option's `value` is an arbitrary object (cascade
                            // descriptors, target records), so it is not a usable React key.
                            key={`opt_${i.toString()}`}
                            label={opt.label}
                            title={opt.detail || undefined}
                            onClick={() => send(opt.label)}
                            clickable={!disabled}
                            disabled={disabled}
                            size="small"
                            variant="outlined"
                            sx={{
                                maxWidth: '100%',
                                height: 'auto',
                                '& .MuiChip-label': { whiteSpace: 'normal', py: 0.4 },
                            }}
                        />
                    ))}
                </Box>
            )}
            {inputs.map((opt, i) => (
                <Box key={`in_${i.toString()}`} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <TextField
                        size="small"
                        fullWidth
                        disabled={disabled}
                        placeholder={opt.placeholder || opt.label}
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                send(typed);
                            }
                        }}
                    />
                    <Button size="small" variant="outlined" disabled={disabled || !typed.trim()} onClick={() => send(typed)}>
                        Go
                    </Button>
                </Box>
            ))}
        </Box>
    );
}
